import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface FamilySubscriptionStatus {
  subscribed: boolean;
  canInvite: boolean;
  isTrialing?: boolean;
  hasEverSubscribed?: boolean;
  product_id: string | null;
  subscription_end: string | null;
  loading: boolean;
  error: string | null;
}

export function useFamilySubscription(familyId?: string) {
  const { user, session } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<FamilySubscriptionStatus>({
    subscribed: false,
    canInvite: false,
    product_id: null,
    subscription_end: null,
    loading: false,
    error: null,
  });

  const [isChecking, setIsChecking] = useState(false);

  const checkSubscription = useCallback(async (force?: boolean) => {
    if (!user || !session || !familyId) {
      setSubscriptionStatus(prev => ({ ...prev, loading: false, subscribed: false, canInvite: false }));
      return;
    }

    // Avoid multiple simultaneous calls
    if (isChecking && !force) {
      console.log('Family subscription check already in progress, skipping');
      return;
    }

    setIsChecking(true);
    setSubscriptionStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase.functions.invoke('check-family-subscription', {
        body: { 
          family_id: familyId 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      setSubscriptionStatus({
        subscribed: data.subscribed || false,
        canInvite: data.canInvite || false,
        isTrialing: data.isTrialing || false,
        hasEverSubscribed: data.hasEverSubscribed || false,
        product_id: data.product_id || null,
        subscription_end: data.subscription_end || null,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Family subscription check error:', error);
      setSubscriptionStatus(prev => ({
        ...prev,
        loading: false,
        error: error.message,
        subscribed: false,
        canInvite: false,
      }));
    } finally {
      setIsChecking(false);
    }
  }, [user, session, familyId]);

  const createFamilyCheckoutSession = useCallback(async (priceId: string) => {
    if (!session || !familyId) {
      throw new Error('User must be authenticated and family must be selected');
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-family-checkout', {
        body: { 
          priceId,
          family_id: familyId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Open checkout in new tab
      if (data.url) {
        window.open(data.url, '_blank');
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create checkout session');
    }
  }, [session, familyId]);

  const openCustomerPortal = useCallback(async () => {
    if (!session || !familyId) {
      throw new Error('User must be authenticated and family must be selected');
    }

    try {
      const { data, error } = await supabase.functions.invoke('family-customer-portal', {
        body: { family_id: familyId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Open portal in new tab
      if (data.url) {
        window.open(data.url, '_blank');
      }

      return data;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to open customer portal');
    }
  }, [session, familyId]);

  const refreshSubscriptionStatus = useCallback(() => {
    if (!isChecking) {
      checkSubscription(true);
    }
  }, [checkSubscription, isChecking]);

  useEffect(() => {
    if (user && session && familyId && !isChecking) {
      checkSubscription();
    }
  }, [user, session, familyId]);

  // Auto-refresh subscription status every 5 minutes
  useEffect(() => {
    if (user && session && familyId) {
      const interval = setInterval(() => {
        if (!isChecking) {
          checkSubscription();
        }
      }, 300000); // 5 minutes
      return () => clearInterval(interval);
    }
  }, [user, session, familyId]);

  return {
    ...subscriptionStatus,
    refreshSubscriptionStatus,
    createFamilyCheckoutSession,
    openCustomerPortal,
  };
}